import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import { StripeContext } from './context/StripeContext';
import { StripeElementsContext } from './context/StripeElementsContext';

export const useStripeInstance = () => useContext(StripeContext);

export const withStripeInstance = (Component) => {
  const WrappedComponent = (props) => {
    const stripe = useStripeInstance();
    return <Component {...props} stripe={stripe} />;
  };

  WrappedComponent.displayName = Component.displayName || Component.name || 'Component';

  return WrappedComponent;
};

export const useStripeElements = () => useContext(StripeElementsContext);

export const withStripeElements = (Component) => {
  const WrappedComponent = (props) => {
    const stripeCheckout = useStripeElements();
    return <Component {...stripeCheckout} {...props} />;
  };

  WrappedComponent.displayName = Component.displayName || Component.name || 'Component';

  return WrappedComponent;
};

export const withReduxForms = (Component) => {
  const WrappedComponent = (props) => {
    const { input, meta, ...baseProps } = props;
    const { error, submitFailed } = meta;
    const isError = error && submitFailed;
    const errorMessage = isError ? get(error, '[0]') : null;

    return <Component
      {...baseProps}
      cardHoldersInputProps={{
        error: isError,
        ...input
      }}
      error={errorMessage}
    />;
  };

  WrappedComponent.displayName = Component.displayName || Component.name || 'Component';
  WrappedComponent.propTypes = {
    input: PropTypes.object,
    meta: PropTypes.object
  };

  return WrappedComponent;
};

export const NEW_CARD = {
  label: 'Add a new card',
  value: 'new_card',
  id: 'new_card',
  testingId: 'new-card-option'
};

export const formatCreditCard = ({ brand, last4, exp_month, exp_year }) => `${brand} - ${last4} (Exp. ${exp_month}/${exp_year})`;

export const StripePaymentElementFonts = [
  {
    family: 'ABCSocial',
    src: "url('https://fonts.peerspace.com/ABCSocial/ABCSocial-Book.otf')",
    weight: 350
  },
  {
    family: 'ABCSocial',
    src: "url('https://fonts.peerspace.com/ABCSocial/ABCSocial-Medium.otf')",
    weight: 500
  }
];

export const StripePaymentElementStyles  = {
  theme: 'minimal',
  variables: {
    borderRadius: '2px',
    fontSizeBase: '16px',
    fontWeightNormal: 350,
    fontFamily: 'ABCSocial, Helvetica Neue, Helvetica, Arial, sans-serif',
    colorTextPlaceholder: '#6A6E7A',
    spacingGridRow: '24px',
    colorPrimary: '#6c30d1',
    colorDanger: '#fb2f3c',
    colorText: '#121212',
    colorIconTabSelected: '#6c30d1'
  },
  rules: {
    '.Input': {
      border: '1px solid #D1D3DB',
      boxShadow: 'none',
      padding: '8px 12px',
      lineHeight: '24px',
      borderRadius: '4px'
    },
    '.Input:focus': {
      border: '1px solid #121212',
      outline: '2px solid #0E6EC8',
      outlineOffset: '1px',
      boxShadow: 'none'
    },
    '.Input--invalid' : {
      boxShadow: 'none',
      color: '#121212'
    },
    '.Label': {
      fontSize: '16px',
      fontFamily: 'ABCSocial, Helvetica Neue, Helvetica, Arial, sans-serif',
      lineHeight: '24px',
      marginBottom: '4px',
      color: '#121212',
      fontWeight: 500
    }
  }
};

export const getMethodMetaById = (id) => {
  return {
    applePay: {
      label: 'Apple Pay',
      image: 'https://assets.peerspace.com/apple-pay-mark.svg?v=rb',
      testingId: 'apple-pay-option'
    },
    googlePay: {
      label: 'Google Pay',
      image: 'https://assets.peerspace.com/google-pay-mark.png?v=rb',
      testingId: 'google-pay-option'
    }
  }[id];
};
